/**
 * @fileoverview Mark-one-notification-read mutation hook.
 * @description Wraps POST /notifications/:id/read (a notification bell dropdown row click) in a
 * TanStack mutation, invalidating the notification history on success so the read state and
 * unread count both update.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { useMutation, useQueryClient } from '@tanstack/react-query'

import { NOTIFICATION_HISTORY_QUERY_KEY } from '@/features/notifications/hooks/useNotificationHistory'
import { NOTIFICATION_UNREAD_COUNT_QUERY_KEY } from '@/features/notifications/hooks/useUnreadNotificationCount'
import { markNotificationRead } from '@/features/notifications/services/notifications.service'

/**
 * @description Marks a single notification as read and refreshes the notification history.
 * @returns {ReturnType<typeof useMutation<void, unknown, string>>} The mutation object (mutate
 * with a notification id, isPending, ...).
 */
export function useMarkNotificationRead() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: markNotificationRead,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: NOTIFICATION_HISTORY_QUERY_KEY })
      void queryClient.invalidateQueries({ queryKey: NOTIFICATION_UNREAD_COUNT_QUERY_KEY })
    },
  })
}
